// REST API client. Access token in memory (zustand); on a 401 we
// transparently refresh once using the stored refresh token (single-flight,
// shared by concurrent callers) and replay the original request. If the
// refresh is rejected the session is dropped so the UI returns to login.

import type {
  ApiError,
  AuthResponse,
  EloResponse,
  LobbyCreateResponse,
  LobbySummaryResponse,
} from "./types";
import { useAuthStore } from "../store/authStore";

const BASE = ""; // same-origin (Vite proxy in dev; nginx in prod)

export class ApiRequestError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Endpoints that must never trigger a transparent refresh: a 401 from these
// is a real credential error (or the refresh call itself, which would loop).
const NO_RETRY_PREFIXES = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/forgot-password",
  "/api/auth/reset-password",
  "/api/auth/validate-reset-token",
];

let refreshPromise: Promise<boolean> | null = null;

/** Single-flight token refresh: concurrent callers share one request. */
export function ensureFreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return Promise.resolve(false);
    refreshPromise = request<AuthResponse>(
      "POST",
      "/api/auth/refresh",
      { refresh_token: refreshToken },
      { retryOn401: false },
    )
      .then((session) => {
        useAuthStore.getState().setSession(session);
        return true;
      })
      .catch((e: unknown) => {
        // The server rejected the refresh (expired / already rotated): the
        // session is dead — drop it so RequireAuth sends the user to login.
        if (e instanceof ApiRequestError && (e.status === 401 || e.status === 403)) {
          useAuthStore.getState().clear();
        }
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { retryOn401?: boolean },
): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (
    res.status === 401 &&
    opts?.retryOn401 !== false &&
    !NO_RETRY_PREFIXES.some((p) => path.startsWith(p))
  ) {
    if (await ensureFreshToken()) {
      return request<T>(method, path, body, { retryOn401: false });
    }
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data as ApiError | undefined;
    if (err?.error) throw new ApiRequestError(err.error.code, err.error.message, res.status);
    throw new ApiRequestError("HTTP_ERROR", `HTTP ${res.status}`, res.status);
  }
  return data as T;
}

export const api = {
  register: (username: string, email: string, password: string) =>
    request<AuthResponse>("POST", "/api/auth/register", { username, email, password }),
  login: (username: string, password: string) =>
    request<AuthResponse>("POST", "/api/auth/login", { username, password }),
  refresh: (refresh_token: string) =>
    request<AuthResponse>("POST", "/api/auth/refresh", { refresh_token }),
  forgotPassword: (email: string) =>
    request<{ message: string }>("POST", "/api/auth/forgot-password", { email }),
  validateResetToken: (token: string) =>
    request<{ valid: boolean }>("GET", `/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`),
  resetPassword: (token: string, new_password: string) =>
    request<{ message: string }>("POST", "/api/auth/reset-password", { token, new_password }),
  me: () => request<{ user_id: string; username: string; enabled: boolean }>("GET", "/api/auth/me"),

  createLobby: (creator_name: string, lobby_name?: string, is_private = false) =>
    request<LobbyCreateResponse>("POST", "/api/lobby/create", { creator_name, lobby_name, is_private }),
  listLobbies: () => request<LobbySummaryResponse[]>("GET", "/api/lobby/list"),
  getLobby: (id: string) => request<unknown>("GET", `/api/lobby/${id}`),
  validateLobby: (id: string) =>
    request<{ exists: boolean; lobby_id: string; status: string; is_private: boolean; player_count: number; max_players: number }>("GET", `/api/lobby/validate/${id}`),
  resetLobby: (id: string) => request<{ status: string; message: string }>("POST", `/api/lobby/${id}/reset`),
  getState: (id: string) => request<import("./types").GameStateView>("GET", `/api/state/${id}`),

  myElo: () => request<EloResponse>("GET", "/api/elo/me"),
  leaderboard: (limit = 100) => request<{ user_id: string; username: string; rating: number; games_played: number; win_rate: number }[]>("GET", `/api/elo/leaderboard?limit=${limit}`),
};